import { createRef, useState, useEffect, useMemo, useRef } from "react";
import { Box, Button, CircularProgress, IconButton, Modal, Paper, Stack, styled, Tooltip, Typography, useTheme } from "@mui/material";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import '@splidejs/react-splide/css';
import { useParams } from "react-router-dom";
import { Close, CloseRounded, DownloadRounded, ErrorRounded, FullscreenRounded, HelpCenterRounded, InfoOutlined, SaveRounded, SourceRounded } from "@mui/icons-material";
import { downloadBlob, downloadFiles, getFileThumbnail, getImageFile } from "../../../lib/services/drive";
import { convertSize } from "../../../lib/commonFunctions/convertSize";
import Toasty from "../../../theme/components/Toasty";
import ProgressButton from "../../common/progressButton";

const Content = ({ files, shareInfo = {} }) => {

    // Initial States
    const { shareFolderId } = useParams();
    const [filesList, setFilesList] = useState(files);
    const multipleFiles = Boolean(files?.length && files.length > 1) | false;
    const mainRef = createRef();
    const thumbsRef = createRef();
    const [selectedFile, setSelectedFile] = useState(files[0]);
    const [fullScreenModalOpen, setFullScreenModalOpen] = useState(false);
    const [infoModalOpen, setInfoModalOpen] = useState(false);
    const [pending, setPending] = useState(false);

    // Options
    const { breakpoints: { values: breakpoints } } = useTheme();
    const mainOptions = useMemo(() => ({
        direction: 'rtl',
        speed: 900,
        perPage: 1,
        perMove: 1,
        pagination: false,
        arrows: multipleFiles
    }), [multipleFiles]);
    const thumbsOptions = useMemo(() => ({
        direction: 'rtl',
        speed: 900,
        rewind: true,
        perPage: 2,
        perMove: 1,
        pagination: false,
        arrows: false,
        gap: 12,
        isNavigation: true,
        mediaQuery: 'min',
        breakpoints: {
            [breakpoints.md]: {
                perPage: 4
            }
        }
    }), []);

    const handleCancelDownload = () => {
        if (!selectedFile) return;
        selectedFile.controller.abort('user canceled');
        const newFile = {...selectedFile}
        newFile.downloadCompleted = false;
        newFile.downloadProgress = 0;
        setSelectedFile(newFile);
        setPending(false);
    }
    const handleDownloadFile = async (fileId, name, isZip) => {
        setPending('downloading');
        if (!selectedFile) return;
        const newFile = {...selectedFile};
        try {
            newFile.downloadProgress = 0;
            newFile.downloadCompleted = false;
            newFile.downloadFailed = false;
            const {data} = await downloadFiles('Share', { fileIds: [fileId], linkAddress: shareFolderId, isZip }, {onProgress: (progress) => setSelectedFile({...newFile, downloadProgress: progress}), signal: newFile.controller?.signal});
            if (!data) return Toasty.error("دانلود فایل با مشکل مواجه شد!");
            if (isZip) {
                const extension = name.split('.').pop();
                let zippedName = "file.zip";
                if (extension) {
                    zippedName = name.replace(`.${extension}`, '.zip');
                }
                await downloadBlob(data, zippedName, isZip);
            } else {
                await downloadBlob(data, name, isZip);
            }
            newFile.downloadCompleted = true;
        } catch (err) {
            if (newFile?.controller?.signal?.reason === "user canceled") {
                newFile.controller = new AbortController();
                return setSelectedFile(newFile);
            }
            newFile.downloadFailed = true;
            console.log(err);
            Toasty.error('خطایی غیر منتظره رخ داد')
        }
        setSelectedFile(newFile);
        setPending(false);
    }

    useEffect(() => {
        if (mainRef.current && thumbsRef.current && thumbsRef.current.splide) {
            mainRef.current.sync(thumbsRef.current.splide);
        }
    }, []);
    useEffect(() => {
        if (files?.length) {
            const newFiles = [...files];
            let updateNeeded = true;
            newFiles.forEach(file => {
                if (file.initialized) return updateNeeded = false;
                file.downloadProgress = 0;
                file.downloadCompleted = false;
                file.downloadFailed = false;
                file.controller = new AbortController();
                file.updated = true;
            });
            if (updateNeeded) setFilesList(newFiles);
        }
    }, [files]);

    // Styles
    const mainSliderStyles = {
        "& .splide__arrow": {
            backgroundColor: "transparent"
        }
    };
    const thumbSliderStyles = {
        mt: 1.5,
        "& .splide__slide": {
            borderRadius: 1,
            "&.is-active": {
                border: theme => `3px solid ${theme.palette.primary.main}!important`
            }
        }
    };

    return (
        <>
            {/* Header */}
            <Box component="header" sx={{ p: 5, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 1, color: "#fff", backgroundColor: theme => theme.palette.primary.main }}>
                <Typography variant="h1" component="h6" fontSize={{ xs: "1.15rem" }} fontWeight={700}>عنوان:</Typography>
                <Typography ml={.5} variant="h1" fontSize={{ xs: "1.15rem" }} fontWeight={700}>{shareInfo.title ? shareInfo.title : "نامشخص"}</Typography>
            </Box>
            {/* Files Slider */}
            <Paper sx={{ mt: 1.5, p: { xs: 2, lg: 2.5 } }}>
                {multipleFiles ? (
                    <>
                        <Box component={Splide} sx={mainSliderStyles} ref={mainRef} options={mainOptions}>
                            {files.map((f, ind) => (
                                <SplideSlide key={ind}>
                                    {renderFile(f)}
                                </SplideSlide>
                            ))}
                        </Box>
                        <Box component={Splide} sx={thumbSliderStyles} ref={thumbsRef} options={thumbsOptions}>
                            {files.map((f, ind) => (
                                <Box component={SplideSlide} key={ind}>
                                    {renderThumb(f, setSelectedFile, setFullScreenModalOpen, setInfoModalOpen)}
                                </Box>
                            ))}
                        </Box>
                    </>
                ) :
                    files && files.length
                        ?
                        <Box position="relative">
                            {renderFile(files[0], true)}
                            {/* Actions */}
                            {isImage(files[0].type) && (
                                <Box sx={{ position: "absolute", top: 0, right: 0 }}>
                                    <Tooltip title="تمام صفحه">
                                        <IconButton onClick={() => { setSelectedFile(files[0]); setFullScreenModalOpen(true) }} color="inherit">
                                            <FullscreenRounded sx={{ fontSize: "2rem" }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}
                            <Box mt={5}>
                                <Stack alignItems="center" spacing={1.5}>
                                    <Stack direction="row" alignItems="center" spacing={.5}>
                                        <Box display="flex" alignItems="center">
                                            <SourceRounded fontSize="small" sx={{ color: theme => theme.palette.primary.main }} />
                                            <Typography ml={.5} fontSize={{ xs: ".925rem", lg: ".975rem" }}>نام فایل:</Typography>
                                        </Box>
                                        <Typography fontSize={{ xs: ".925rem", lg: ".975rem" }} fontWeight={500}>{files[0].name}</Typography>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={.5}>
                                        <Box display="flex" alignItems="center">
                                            <SaveRounded fontSize="small" sx={{ color: theme => theme.palette.primary.main }} />
                                            <Typography ml={.5} fontSize={{ xs: ".925rem", lg: ".975rem" }}>حجم:</Typography>
                                        </Box>
                                        <Typography fontSize={{ xs: ".925rem", lg: ".975rem" }} fontWeight={500}>{convertSize(files[0].size, { farsi: true })}</Typography>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={.5}>
                                        <Box display="flex" alignItems="center">
                                            <HelpCenterRounded fontSize="small" sx={{ color: theme => theme.palette.primary.main }} />
                                            <Typography ml={.5} fontSize={{ xs: ".925rem", lg: ".975rem" }}>فرمت:</Typography>
                                        </Box>
                                        <Typography fontSize={{ xs: ".925rem", lg: ".975rem" }} fontWeight={500}>{files[0]?.type ? files[0].type.toUpperCase() : "نامشخص"}</Typography>
                                    </Stack>
                                </Stack>
                                <Box display="flex" justifyContent="center">
                                    <ProgressButton
                                        downloading={pending === "downloading"}
                                        progress={selectedFile.downloadProgress}
                                        isCompleted={selectedFile.downloadCompleted}
                                        isFailed={selectedFile.downloadFailed}
                                        onClick={() => handleDownloadFile(selectedFile.id, selectedFile.name, false)}
                                        onCancel={handleCancelDownload}
                                        fullWidth color="success"
                                        variant="contained" disableElevation
                                        wrapperProps={{ mt: 2, width: {xs: "100%", lg: "auto"} }}
                                        sx={{ py: 1.5, px: { lg: 12 }, width: { xs: "100%", lg: "auto" } }}>دانلود</ProgressButton>
                                </Box>
                            </Box>
                        </Box>
                        :
                        <Box py={17.775} display='flex' alignItems="center" justifyContent="center" flexDirection="column">
                            <ErrorRounded color="warning" sx={{ fontSize: "3.5rem" }} />
                            <Typography mt={1} fontSize={{ xs: "1.1rem", lg: "1.15rem" }} fontWeight={600}>هیچ فایلی یافت نشد</Typography>
                        </Box>
                }
            </Paper>
            {/* Fullscreen Modal */}
            <Modal open={fullScreenModalOpen} onClose={() => setFullScreenModalOpen(false)} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column">
                    {selectedFile && (
                        <Box component="img" src={getImageFile(selectedFile.fileHashId)} sx={{ maxWidth: "85vw", maxHeight: "85vh", objectFit: "contain" }} />
                    )}
                    <Button onClick={() => setFullScreenModalOpen(false)} variant="contained" color="error" sx={{ width: { xs: "100%", lg: "auto" }, mt: 2, py: 1.2, px: { lg: 6 } }} disableElevation endIcon={<Close />}>بستن</Button>
                </Box>
            </Modal>
            {/* File Info Modal */}
            <Modal open={infoModalOpen} onClose={() => setInfoModalOpen(false)} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Paper sx={{ p: 2.5, width: { xs: "90%", lg: "25%" } }}>
                    {selectedFile && (
                        <>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography fontSize={{ xs: "1rem" }} fontWeight={500} textAlign="center">مشخصات فایل</Typography>
                                <IconButton onClick={() => setInfoModalOpen(false)}><CloseRounded /></IconButton>
                            </Box>
                            <Stack mt={2} spacing={1}>
                                <Stack direction="row" alignItems="center" spacing={.5}>
                                    <Typography fontSize={{ xs: ".975rem" }}>نام فایل:</Typography>
                                    <Typography fontSize={{ xs: ".95rem" }} fontWeight={300}>{selectedFile.name}</Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={.5}>
                                    <Typography fontSize={{ xs: ".975rem" }}>حجم:</Typography>
                                    <Typography fontSize={{ xs: ".95rem" }} fontWeight={300}>{convertSize(selectedFile.size, { farsi: true })}</Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={.5}>
                                    <Typography fontSize={{ xs: ".975rem" }}>فرمت:</Typography>
                                    <Typography fontSize={{ xs: ".95rem" }} fontWeight={300}>{selectedFile.type}</Typography>
                                </Stack>
                            </Stack>
                            <ProgressButton
                                downloading={pending === "downloading"}
                                progress={selectedFile.downloadProgress}
                                isCompleted={selectedFile.downloadCompleted}
                                isFailed={selectedFile.downloadFailed}
                                onClick={() => handleDownloadFile(selectedFile.id, selectedFile.name, false)}
                                onCancel={handleCancelDownload}
                                fullWidth color="success"
                                variant="contained" disableElevation
                                sx={{ py: 1.25 }} wrapperProps={{ mt: 1.5 }}
                                startIcon={<DownloadRounded />}>دانلود</ProgressButton>
                        </>
                    )}
                </Paper>
            </Modal>
        </>
    )
}
export default Content;

const isImage = (type) => {
    switch (type) {
        case 'jpg':
        case 'jpeg':
        case 'gif':
        case 'png': return true
        default: return false
    }
}

const renderFile = (file, isSingle) => {

    const { type } = file;

    switch (type) {
        // Images
        case 'jpg':
        case 'jpeg':
        case 'gif':
        case 'png':
            return (
                <Box width={1} height={{ xs: "50vw", lg: "35vw", xxl: "30vw" }}>
                    <Box component="img" alt="cover" src={getImageFile(file.fileHashId)} width={1} height={1} sx={{ objectFit: "contain", borderRadius: 1 }} />
                </Box>
            )

        // Videos
        // case 'mp4':
        // case 'mov':
        // case 'webm': return (
        //     <Box width={1} height={{ xs: "50vw", lg: "35vw", xxl: "30vw" }}>
        //         <RenderVideo file={file} />
        //     </Box>
        // )

        // Music
        // case 'mp3':
        // case 'ogg':
        // case 'mpeg':
        // case 'wav': return (
        //     <Box width={1} height={{ xs: "50vw", lg: "35vw", xxl: "30vw" }} display="flex" alignItems="center" justifyContent="center" flexDirection="column">
        //         <Typography fontSize={{ xs: "1.2rem" }} fontWeight={600} textAlign="center">{file.name}</Typography>
        //         <Box mt={2} component="audio" controls>
        //             <source src={file.src} type="audio/ogg" />
        //             <source src={file.src} type="audio/mpeg" />
        //             Your browser does not support the audio element.
        //         </Box>
        //     </Box>
        // )

        // Others
        default:
            return (
                <Box width={1} height={{ xs: "50vw", lg: "25vw", xxl: "20vw" }} borderRadius="inherit" sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                    <UnsupportedFile fileType={type} />
                    {!isSingle && (
                        <Typography mt={2} fontSize={{ xs: "1.2rem" }} fontWeight={600} textAlign="center">{file.name}</Typography>
                    )}
                </Box>
            )
    }
}
const renderThumb = (file, setSelectedFile, setFullScreenModalOpen, setInfoModalOpen) => {

    const { type } = file;
    const Actions = (props) => {

        const { file, ...restProps } = props;
        const StyledButton = styled(IconButton)(({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            opacity: .6,
            transition: "opacity .2s ease",
            "&:hover": {
                backgroundColor: theme.palette.background.paper,
                opacity: 1,
            }
        }));

        return (
            <Stack p={1} direction="row" alignItems="center" sx={{ position: "absolute", bottom: 0, left: 0, right: 0 }} spacing={2} {...restProps}>
                {isImage(file.type) && (
                    <Tooltip title="تمام صفحه">
                        <StyledButton onClick={() => { setSelectedFile(file); setFullScreenModalOpen(true) }}><FullscreenRounded fontSize="small" /></StyledButton>
                    </Tooltip>
                )}
                <Tooltip title="مشخصات">
                    <StyledButton onClick={() => { setSelectedFile(file); setInfoModalOpen(true) }}><InfoOutlined fontSize="small" /></StyledButton>
                </Tooltip>
            </Stack>
        )
    }

    switch (type) {
        // Images
        case 'jpg':
        case 'jpeg':
        case 'gif':
        case 'png':
            return (
                <Box width={1} height={{ xs: "25vw", lg: "10vw" }} borderRadius="inherit" position="relative">
                    <Box component="img" alt="cover" src={getFileThumbnail(file.fileHashId)} width={1} height={1} sx={{ objectFit: "cover", borderRadius: "inherit", border: "1px solid #d1d1d1" }} />
                    <Actions file={file} />
                </Box>
            )

        // Videos
        // case 'mp4':
        // case 'webm':
        // case 'ogg': return (
        //     <Box width={1} height={{ xs: "25vw", lg: "10vw" }} borderRadius="inherit" position="relative">
        //         {/*<Box component="video" alt="cover" width={1} height={1} sx={{objectFit: "cover", borderRadius: "inherit"}} controls={false} muted>*/}
        //         {/*    <source src={file.src} type="video/mp4"/>*/}
        //         {/*    <source src={file.src} type="video/ogg"/>*/}
        //         {/*    Your browser does not support the video tag.*/}
        //         {/*</Box>*/}
        //         <RenderVideo file={file} controls={false} />
        //         <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}><PlayCircleFilledRounded sx={{ color: "#fff" }} fontSize="large" /></Box>
        //         <Actions file={file} />
        //     </Box>
        // )

        // Others
        default:
            return (
                <Box width={1} height={{ xs: "25vw", lg: "10vw" }} borderRadius="inherit" position="relative" sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #d1d1d1" }}>
                    <UnsupportedFile fileType={type} />
                    <Actions p={1} file={file} />
                </Box>
            )
    }
}
const UnsupportedFile = ({ fileType }) => {

    const File = ({ name }) => <Box component="img" alt="icon" src={`/assets/image/icon/file/${name}`} width={1} height={1} sx={{ objectFit: "contain" }} />

    switch (fileType) {
        case 'ai': return <File name="ai.svg" />
        case 'avi': return <File name="avi.svg" />
        case 'doc': return <File name="doc.svg" />
        case 'mkv': return <File name="mkv.svg" />
        case 'pdf': return <File name="pdf.svg" />
        case 'ppt': return <File name="ppt.svg" />
        case 'psd': return <File name="psd.svg" />
        case 'txt': return <File name="txt.svg" />
        case 'xls': return <File name="xls.svg" />
        case 'zip': return <File name="zip.svg" />
        case 'mp3': return <File name="mp3.svg" />
        case 'svg': return <File name="svg.svg" />
        default: return <File name="file.svg" />
    }
}

const RenderVideo = (props) => {

    const { file, ...restProps } = props

    const [src, setSrc] = useState(null);
    const [loading, setLoading] = useState(true);
    const videoRef = useRef(null);

    useEffect(() => {
        // getVideoSrc(file.folderId, file.id)
        //     .then(r => {
        //         setSrc(r);
        //         setLoading(false);
        //         videoRef.current?.load();
        //     })
    }, []);

    return (
        !loading
            ?
            <Box component="video" alt="cover" width={1} height={1} sx={{ objectFit: "cover", borderRadius: "inherit" }} controls ref={videoRef} {...restProps}>
                <source src={src} type="video/mp4" />
                <source src={src} type="video/ogg" />
                Your browser does not support the video tag.
            </Box>
            :
            <Box sx={{ width: 1, height: 1, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "inherit", background: "rgb(0 0 0 / 25%)" }}>
                <Typography mr={1} fontSize=".985rem" fontWeight={600}>در حال بارگذاری..</Typography>
                <CircularProgress size={26} />
            </Box>
    )
}
